import React from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Grid } from "@mui/material";
import { useState } from "react";
import { useEffect } from "react";
import {
  GlobalConfiguration,
  ScreenSizeConfiguration,
  TimelineConfiguration,
} from "../../utils/configurationProvider";

export default function BarClick({
  viewTxnDetails,
  type,
  handleCardClick,
  handleCardDistributionClick,
}) {
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= ScreenSizeConfiguration.mobileScreen,
  );
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= ScreenSizeConfiguration.mobileScreen);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [window.innerWidth]);
  const stageColors = {
    Created: "var(--queued)",
    Approved: "var(--positive-bg)",
    awaiting_funds: "var(--awaiting)",
    DistributionInitiated: "var(--emphasis)",
    LedgerDebitEntry: "var(--pending)",
    DistributionComplete: "var(--settled)",
    Rejected: "var(--negative-act)",
    Abandoned: "var(--abandoned)",
  };

  const actionColors = [
    "var(--queued)",
    "var(--abandoned)",
    "var(--emphasis)",
    "var(--pending)",
    "var(--awaiting)",
  ];

  // Helper to format duration
  const formatDuration = (ms) => {
    if (ms <= 0) return "0s";

    if (ms < TimelineConfiguration.totalSeconds) return "1s";

    const seconds = Math.floor(ms / TimelineConfiguration.totalSeconds);
    const minutes = Math.floor(seconds / TimelineConfiguration.oneHour);
    const hours = Math.floor(minutes / TimelineConfiguration.oneHour);
    const days = Math.floor(hours / TimelineConfiguration.oneDay);

    if (days > 0) {
      const remHours = hours % TimelineConfiguration.oneDay;
      const remMinutes = minutes % TimelineConfiguration.oneHour;
      return `${days}d ${remHours}h ${remMinutes}m`;
    } else if (hours > 0) {
      const remMinutes = minutes % TimelineConfiguration.oneHour;
      return `${hours}h ${remMinutes}m`;
    } else if (minutes > 0) {
      const remSeconds = seconds % TimelineConfiguration.oneHour;
      return `${minutes}m ${remSeconds}s`;
    } else {
      const remMs = ms % TimelineConfiguration.totalSeconds;
      return remMs > 0 ? `${seconds}s ${remMs}ms` : `${seconds}s`;
    }
  };

  const minWidthPct = isMobile
    ? TimelineConfiguration.barWidthLarge
    : TimelineConfiguration.barWidthMedium;

  const logs = viewTxnDetails?.[0]?.distribution_logs || [];

  const timeDiffs = logs.map((stage, index) => {
    if (index === 0) return 0;
    const prev = new Date(logs[index - 1].timestamp);
    const curr = new Date(stage.timestamp);
    return curr - prev;
  });
  const totalDuration = timeDiffs.reduce((a, b) => a + b, 0);
  const widths =
    logs.length === 1
      ? [TimelineConfiguration.timeDuration]
      : timeDiffs.map((diff) => {
          let pct = totalDuration
            ? (diff / totalDuration) * TimelineConfiguration.timeDuration
            : 0;
          if (pct < minWidthPct) pct = minWidthPct;
          return pct;
        });

  const minWidthPct1 = isMobile ? TimelineConfiguration.barWidthLarge : 0;
  const logss = viewTxnDetails?.[0]?.logs || [];
  if (!logss.length) return null;

  const sortedLogs = [...logss].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
  );

  const firstTime = Math.min(
    ...sortedLogs.map((l) => new Date(l.timestamp).getTime()),
  );
  const lastTime = Math.max(
    ...sortedLogs.map((l) => new Date(l.timestamp).getTime()),
  );
  const totalTimeline = lastTime - firstTime || 1;

  // calculate raw widths
  let rawWidths = sortedLogs.map((log) => {
    const time = new Date(log.timestamp).getTime();
    return (
      ((time - firstTime) / totalTimeline) * TimelineConfiguration.timeDuration
    );
  });

  // for multiple logs with same timestamp, give equal width
  rawWidths = rawWidths.map((w, i, arr) => {
    if (i === 0) return 0; // first left will be 0
    return w - arr[i - 1];
  });

  // apply minWidth & adjust
  let widths1 = [...rawWidths];
  let belowMinIndexes = widths1
    .map((w, i) => (w < minWidthPct1 ? i : -1))
    .filter((i) => i !== -1);
  let remainingIndexes = widths1
    .map((w, i) => (w >= minWidthPct1 ? i : -1))
    .filter((i) => i !== -1);

  belowMinIndexes.forEach((i) => (widths1[i] = minWidthPct1));
  let totalAssigned = widths1.reduce((sum, w) => sum + w, 0);
  let excess = totalAssigned - TimelineConfiguration.timeDuration;
  if (excess > 0 && remainingIndexes.length) {
    let remainingTotal = remainingIndexes.reduce(
      (sum, i) => sum + widths1[i],
      0,
    );
    remainingIndexes.forEach((i) => {
      widths1[i] -= (widths1[i] / remainingTotal) * excess;
    });
  }

  const gap = TimelineConfiguration.barwidthBase;
  const totalWidth = widths1.reduce((a, b) => a + b, 0);
  const totalGaps = (sortedLogs.length - 1) * gap;
  const scale = (TimelineConfiguration.timeDuration - totalGaps) / totalWidth;

  let accumulated = 0;
  let previousTime = null;
  let cumulativeTime = 0;

  const timeline = sortedLogs.map((log, idx) => {
    const logTime = new Date(log.timestamp).getTime();
    let incremental = 0;

    if (previousTime) {
      incremental = logTime - previousTime;
    }

    cumulativeTime += incremental;
    previousTime = logTime;

    const leftPct = widths1.slice(0, idx).reduce((acc, w, i) => {
      return i === 0 ? acc + w * scale + gap : acc + w * scale;
    }, 0);

    return {
      ...log,
      incremental,
      cumulativeTime,
      widthPct: widths1[idx] * scale,
      leftPct,
    };
  });

  const filteredLogs = logs.filter((stage, index, arr) => {
    if (stage.action === "DistributionInitiated") {
      return (
        index === arr.map((s) => s.action).lastIndexOf("DistributionInitiated")
      );
    }
    return true;
  });

  return (
    <>
      {type === "DistributionTimeLine" && (
        <>
          <div className=" flex">
            <div className=" mt-margin-xl pt-padding-md text-text-secondary text-font-xxs font-normal">
              Status
            </div>
            <Box
              sx={{
                width: "var(--width-full)",
                padding: "var(--padding-sm)",
                borderRadius: "var(--border-radius-sm)",
                position: "relative", // needed for absolute axis lines
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "var(--gap-md)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    flex: 1,
                    position: "relative",
                  }}
                >
                  {/* Y-axis */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: "var(--left-base)", // true origin
                      top: "var(--top-base)",
                      bottom: "var(--icon-sm)", // leave room for X-axis
                      width: "var(--border)",
                      bgcolor: "var(--text-primary)",
                      opacity: "var(--lightest-opacity)",
                    }}
                  />
                  {/* X-axis */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: "var(--left-base)", // match Y-axis origin
                      right: "var(--right-base)",
                      bottom: "var(--icon-sm)",
                      height: "var(--border)",
                      bgcolor: "var(--text-primary)",
                      opacity: "var(--lightest-opacity)",
                    }}
                  />

                  {/* Chart Content */}
                  <Box
                    sx={{
                      width: "var(--width-full)",
                      mt: "var(--left-sm)",
                    }}
                  >
                    {/* Graph bars */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        paddingLeft: "var(--border)",
                        height: "var(--multiandsingleview-btn-width)",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      {logs?.map((stage, index) => {
                        const widthPct = `${widths[index]}%`;

                        return (
                          <Tooltip
                            key={index}
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: "var(--primary-black)",
                                  color: "var(--text-primary)",
                                },
                              },
                            }}
                            title={
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color:
                                      stageColors[stage.action] ||
                                      "var(--abandoned)",
                                  }}
                                >
                                  {stage.action}
                                </Typography>
                                <Typography variant="caption">
                                  {new Date(stage.timestamp).toLocaleString(
                                    "en-GB",
                                    {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                      hour12: true,
                                    },
                                  )}
                                </Typography>
                              </Box>
                            }
                            placement="top"
                          >
                            <Box
                              onClick={() =>
                                handleCardDistributionClick(
                                  logs.length - index - 1,
                                )
                              }
                              className={
                                "global-hover-white relative z-z-index-base"
                              }
                              sx={{
                                width: widthPct,
                                bgcolor:
                                  stageColors[stage.action] ||
                                  "var(--abandoned)",
                                cursor: "pointer",
                                opacity: "var(--light-opacity)",
                                transition: "var(--animation-fast-s)",
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Box>

                    {/* Labels */}
                    <Box
                      sx={{
                        position: "relative",
                        mt: "var(--margin-sm)",
                        height: "var(--icon-lg)",
                      }}
                    >
                      {logs.map((stage, index) => {
                        const baseDate = new Date(logs[0].timestamp);
                        const logDate = new Date(stage.timestamp);
                        const diffDays = Math.floor(
                          (logDate - baseDate) /
                            (TimelineConfiguration.totalSeconds *
                              TimelineConfiguration.oneHour *
                              TimelineConfiguration.oneHour *
                              TimelineConfiguration.oneDay),
                        );
                        const dayLabel = `Day ${diffDays + 1}`;
                        const leftPct =
                          index === logs.length - 1
                            ? TimelineConfiguration.timeDuration
                            : ((index + 1) / logs.length) *
                              TimelineConfiguration.timeDuration;

                        return <React.Fragment key={index}></React.Fragment>;
                      })}
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  justifyContent: "center",
                  overflowX: "auto",
                  gap: isMobile ? "var(--gap-sm)" : "var(--gap-md) !important",
                }}
              >
                {filteredLogs.map((stage, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: !isMobile && "center",
                      gap: "var(--gap-xs) !important",
                    }}
                  >
                    <Box
                      sx={{
                        width: "var(--icon-sm)",
                        height: "var(--icon-sm)",
                        bgcolor: stageColors[stage.action],
                        borderRadius: "var(--borderthickness)",
                        opacity: "var(--light-opacity)",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "var(--text-primary)" }}
                    >
                      {stage.action}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {timeline.length !== GlobalConfiguration?.lengthCheckOne &&
                totalDuration !== GlobalConfiguration?.lengthCheckZero && (
                  <div className="flex justify-center mt-margin-xs">
                    <span className="text-text-secondary text-font-xs font-normal">
                      {formatDuration(totalDuration)}
                    </span>
                  </div>
                )}
            </Box>
          </div>
        </>
      )}

      {type === "ApprovalTimeline" && (
        <Box
          sx={{
            marginTop: "var(--padding-xl)",
            color: "white",
            position: "relative",
            height: "auto",
          }}
        >
          {/* Y-axis */}
          <Box
            sx={{
              position: "absolute",
              top: "var(--top-base)",
              bottom: "var(--bottom-base)",
              opacity: "var(--lightest-opacity)",
              left: isMobile
                ? "var(--width-label)"
                : "var(--list-view-amount-width)",
              borderLeft: "var(--border) solid var(--text-primary)",
            }}
          />

          {/* X-axis */}
          <Box
            sx={{
              position: "absolute",
              left: isMobile
                ? "var(--width-label)"
                : "var(--list-view-amount-width)",
              right: "var(--right-base)",
              bottom: "var(--bottom-base)",
              opacity: "var(--lightest-opacity)",
              borderTop: "var(--border) solid var(--text-primary)",
            }}
          />

          <Grid
            container
            direction="column-reverse"
          >
            {timeline?.map((log, idx) => (
              <Grid
                item
                key={idx}
              >
                <Box
                  display="flex"
                  sx={{
                    paddingLeft: isMobile
                      ? "var(--padding-md)"
                      : "var(--padding-md)",
                  }}
                  alignItems="center"
                  onClick={() => handleCardClick(timeline.length - idx - 1)}
                >
                  {/* Y-axis label */}
                  <Box
                    width={
                      isMobile ? "var(--timeline-top)" : "var(--width-label)"
                    }
                    marginRight={!isMobile && "var(--margin-md)"}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "var(--font-xxs)",
                        fontWeight: "var(--font-semibold)",
                        textAlign: "right",
                      }}
                      className={`truncate text-text-secondary  pl-margin-xs ${isMobile ? " w-width-lg" : "w-pop-up-width-4"} `}
                    >
                      {log.user_full_name}
                    </Typography>
                  </Box>

                  <Box
                    flex={1}
                    sx={{
                      position: "relative",
                      marginTop: idx % 2 === 0 && "var(--margin-sm)",
                      marginBottom: idx % 2 === 0 && "var(--margin-sm)",
                      marginLeft: isMobile ? "" : "var(--margin-sm)",
                    }}
                  >
                    <Tooltip
                      title={
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: "var(--font-semibold)",
                              fontSize: "var(--font-xs)",
                            }}
                            variant="body2"
                          >
                            {log.user_full_name}
                            {timeline.length !== 1 && idx > 0 && (
                              <span>
                                ({formatDuration(log.cumulativeTime)})
                              </span>
                            )}
                          </Typography>

                          <Typography
                            sx={{ fontSize: "var(--font-xs)" }}
                            variant="body2"
                          >
                            Approved on: <br />
                            {new Date(log.timestamp).toLocaleString("en-GB", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}
                          </Typography>
                        </Box>
                      }
                      placement="top"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: "var(--primary-black)",
                            color: "var(--text-primary)",
                            fontSize: "var(--font-xxs)",
                          },
                        },
                      }}
                    >
                      <Box
                        className={
                          "global-hover-white relative px-padding-xs py-padding-sm z-z-index-base"
                        }
                        sx={{
                          left: `${log.leftPct}%`,
                          width: `${log.widthPct}%`,
                          bgcolor: actionColors[idx % actionColors.length],
                          opacity: "var(--light-opacity)",
                          cursor: "pointer",
                        }}
                      />
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </>
  );
}
